import React, { useMemo, useState, Fragment, Component } from 'react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Toggle } from '../components/ui/Toggle';
import { Badge } from '../components/ui/Badge';
import { Search, Save, ImageOff, MoreVertical, Edit, X, ChevronRight, Home, Grid3x3, List, ChevronDown, Globe, MapPin, Building, RefreshCw, Undo2, Check, Star, DollarSign } from 'lucide-react';
// --- Types ---
type ScopeLevel = 'global' | 'region' | 'community';
type Scope = {
  level: ScopeLevel;
  regionId?: string;
  communityId?: string;
  name: string;
};
type GlobalOption = {
  id: string;
  name: string;
  category: string;
  subcategory: string;
  variantSet: string;
  basePrice: number;
  description: string;
  enabled: boolean;
  isStandard: boolean; // Global default for standard inclusion
  hasImage: boolean;
  imageUrl?: string;
  tags?: string[];
};
type OptionOverride = {
  optionId: string;
  level: 'region' | 'community';
  levelId: string;
  priceOverride?: number;
  enabledOverride?: boolean;
  nameOverride?: string;
  descriptionOverride?: string;
  isStandardOverride?: boolean; // Can override standard status at region/community level
};
type ResolvedOption = GlobalOption & {
  effectivePrice: number;
  effectiveEnabled: boolean;
  effectiveName: string;
  effectiveDescription: string;
  effectiveIsStandard: boolean;
  overrideSource: 'global' | 'region' | 'community';
  hasOverride: boolean;
  parentValue?: {
    price: number;
    enabled: boolean;
    name: string;
    description: string;
    isStandard: boolean;
  };
};
type NavigationLevel = 'category' | 'subcategory' | 'variantSet' | 'options';
type BreadcrumbItem = {
  label: string;
  level: NavigationLevel;
  value: string;
};
// --- Mock Data ---
const regions = [{
  id: 'alberta',
  name: 'Alberta Region'
}, {
  id: 'ontario',
  name: 'Ontario Region'
}, {
  id: 'bc',
  name: 'BC Region'
}, {
  id: 'texas',
  name: 'Texas Region'
}, {
  id: 'florida',
  name: 'Florida Region'
}, {
  id: 'arizona',
  name: 'Arizona Region'
}];
const communities: Record<string, {
  id: string;
  name: string;
}[]> = {
  alberta: [{
    id: 'oakwood',
    name: 'Oakwood Estates'
  }, {
    id: 'maple',
    name: 'Maple Ridge'
  }, {
    id: 'pine',
    name: 'Pine Valley'
  }, {
    id: 'cedar',
    name: 'Cedar Heights'
  }],
  ontario: [{
    id: 'lakeside',
    name: 'Lakeside Living'
  }, {
    id: 'urban',
    name: 'Urban Heights'
  }, {
    id: 'riverdale',
    name: 'Riverdale Commons'
  }],
  bc: [{
    id: 'mountain',
    name: 'Mountain View'
  }, {
    id: 'pacific',
    name: 'Pacific Point'
  }, {
    id: 'coastal',
    name: 'Coastal Breeze'
  }],
  texas: [{
    id: 'austin-hills',
    name: 'Austin Hills'
  }, {
    id: 'dallas-park',
    name: 'Dallas Park'
  }, {
    id: 'houston-meadows',
    name: 'Houston Meadows'
  }],
  florida: [{
    id: 'miami-shores',
    name: 'Miami Shores'
  }, {
    id: 'tampa-bay',
    name: 'Tampa Bay Estates'
  }, {
    id: 'orlando-gardens',
    name: 'Orlando Gardens'
  }],
  arizona: [{
    id: 'phoenix-ridge',
    name: 'Phoenix Ridge'
  }, {
    id: 'scottsdale-heights',
    name: 'Scottsdale Heights'
  }]
};
// Initial Global Options
const initialGlobalOptions: GlobalOption[] = [{
  id: 'model-1',
  name: 'The Lawson',
  category: 'Models',
  subcategory: 'The Lawson',
  variantSet: 'Model Details',
  enabled: true,
  isStandard: false,
  basePrice: 600000,
  description: '1795 sq ft • 3 beds • 2.5 baths • Two storey laned home',
  hasImage: true,
  imageUrl: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=400&q=80',
  tags: ['Calgary', 'Two Storey', 'Laned']
}, {
  id: 'ext-1',
  name: 'Black Asphalt Shingles',
  category: 'Exterior',
  subcategory: 'Roofing',
  variantSet: 'Shingles',
  enabled: true,
  isStandard: true,
  basePrice: 0,
  description: 'Classic black asphalt shingles with 30-year warranty',
  hasImage: true,
  imageUrl: 'https://cdn.magicpatterns.com/uploads/gy4nLAmMDnxDZrtDgT1xUc/black-asphalt.jpg',
  tags: ['Asphalt', '30-year warranty']
}, {
  id: 'ext-2',
  name: 'Gray Asphalt Shingles',
  category: 'Exterior',
  subcategory: 'Roofing',
  variantSet: 'Shingles',
  enabled: true,
  isStandard: false,
  basePrice: 1200,
  description: 'Modern gray asphalt shingles - upgrade option',
  hasImage: true,
  imageUrl: 'https://cdn.magicpatterns.com/uploads/7fXbSwM5gVoriK8Hvp5r78/gray-asphalt.jpg',
  tags: ['Asphalt', 'Modern']
}, {
  id: 'int-1',
  name: 'Stainless Steel Appliances',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Appliance Package',
  enabled: true,
  isStandard: false,
  basePrice: 2500,
  description: 'Premium stainless steel appliance package',
  hasImage: true,
  imageUrl: 'https://cdn.magicpatterns.com/uploads/uz3yKu22Y2YDi2VtcShnwy/stainless-steel-fridge.jpg',
  tags: ['Appliances', 'Stainless']
}, {
  id: 'int-2',
  name: 'Black + White Appliances',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Appliance Package',
  enabled: true,
  isStandard: true,
  basePrice: 0,
  description: 'Classic black and white appliance package - included',
  hasImage: false,
  tags: ['Appliances', 'Basic']
}, {
  id: 'int-3',
  name: 'Built-in Appliances',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Appliance Package',
  enabled: true,
  isStandard: false,
  basePrice: 8500,
  description: 'Premium built-in appliance package with panel-ready fronts',
  hasImage: false,
  tags: ['Appliances', 'Built-in', 'Premium']
}, {
  id: 'int-6',
  name: 'White Quartz Countertops',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Countertops',
  enabled: true,
  isStandard: false,
  basePrice: 4500,
  description: 'Classic white quartz countertops',
  hasImage: false,
  tags: ['Quartz', 'Classic']
}, {
  id: 'int-7',
  name: 'Laminate Countertops',
  category: 'Interior',
  subcategory: 'Kitchen',
  variantSet: 'Countertops',
  enabled: true,
  isStandard: true,
  basePrice: 0,
  description: 'Standard laminate countertops - included',
  hasImage: false,
  tags: ['Laminate', 'Standard']
}, {
  id: 'int-8',
  name: 'Natural Oak Hardwood',
  category: 'Interior',
  subcategory: 'Flooring',
  variantSet: 'Hardwood',
  enabled: true,
  isStandard: false,
  basePrice: 8500,
  description: 'Classic natural oak hardwood flooring',
  hasImage: false,
  tags: ['Hardwood', 'Oak', 'Natural']
}];
// Initial Overrides - Including standard overrides
const initialOverrides: OptionOverride[] = [
// Alberta Region: Cheaper hardwood
{
  optionId: 'int-8',
  level: 'region',
  levelId: 'alberta',
  priceOverride: 7200
},
// Oakwood Estates: Stainless steel is STANDARD (included) here!
{
  optionId: 'int-1',
  level: 'community',
  levelId: 'oakwood',
  isStandardOverride: true,
  priceOverride: 0
},
// Cedar Heights: Built-in appliances are STANDARD here (luxury community)
{
  optionId: 'int-3',
  level: 'community',
  levelId: 'cedar',
  isStandardOverride: true,
  priceOverride: 0
}, {
  optionId: 'int-2',
  level: 'community',
  levelId: 'cedar',
  isStandardOverride: false,
  priceOverride: 0
},
// Ontario Region: Quartz is more expensive
{
  optionId: 'int-6',
  level: 'region',
  levelId: 'ontario',
  priceOverride: 5200
},
// Maple Ridge: Gray shingles disabled
{
  optionId: 'ext-2',
  level: 'community',
  levelId: 'maple',
  enabledOverride: false
}];
// --- Helper Functions ---
const resolveOption = (globalOption: GlobalOption, scope: Scope, overrides: OptionOverride[]): ResolvedOption => {
  let effectivePrice = globalOption.basePrice;
  let effectiveEnabled = globalOption.enabled;
  let effectiveName = globalOption.name;
  let effectiveDescription = globalOption.description;
  let effectiveIsStandard = globalOption.isStandard;
  let overrideSource: 'global' | 'region' | 'community' = 'global';
  let hasOverride = false;
  let parentValue = {
    price: globalOption.basePrice,
    enabled: globalOption.enabled,
    name: globalOption.name,
    description: globalOption.description,
    isStandard: globalOption.isStandard
  };
  // Check Region Override
  if (scope.level !== 'global' && scope.regionId) {
    const regionOverride = overrides.find(o => o.optionId === globalOption.id && o.level === 'region' && o.levelId === scope.regionId);
    if (regionOverride) {
      if (regionOverride.priceOverride !== undefined) effectivePrice = regionOverride.priceOverride;
      if (regionOverride.enabledOverride !== undefined) effectiveEnabled = regionOverride.enabledOverride;
      if (regionOverride.nameOverride !== undefined) effectiveName = regionOverride.nameOverride;
      if (regionOverride.descriptionOverride !== undefined) effectiveDescription = regionOverride.descriptionOverride;
      if (regionOverride.isStandardOverride !== undefined) effectiveIsStandard = regionOverride.isStandardOverride;
      if (scope.level === 'region') {
        overrideSource = 'region';
        hasOverride = true;
      } else {
        overrideSource = 'region';
        parentValue = {
          price: effectivePrice,
          enabled: effectiveEnabled,
          name: effectiveName,
          description: effectiveDescription,
          isStandard: effectiveIsStandard
        };
      }
    }
  }
  // Check Community Override (wins over region)
  if (scope.level === 'community' && scope.communityId) {
    const communityOverride = overrides.find(o => o.optionId === globalOption.id && o.level === 'community' && o.levelId === scope.communityId);
    if (communityOverride) {
      if (communityOverride.priceOverride !== undefined) effectivePrice = communityOverride.priceOverride;
      if (communityOverride.enabledOverride !== undefined) effectiveEnabled = communityOverride.enabledOverride;
      if (communityOverride.nameOverride !== undefined) effectiveName = communityOverride.nameOverride;
      if (communityOverride.descriptionOverride !== undefined) effectiveDescription = communityOverride.descriptionOverride;
      if (communityOverride.isStandardOverride !== undefined) effectiveIsStandard = communityOverride.isStandardOverride;
      overrideSource = 'community';
      hasOverride = true;
    }
  }
  return {
    ...globalOption,
    effectivePrice,
    effectiveEnabled,
    effectiveName,
    effectiveDescription,
    effectiveIsStandard,
    overrideSource,
    hasOverride,
    parentValue: hasOverride ? parentValue : undefined
  };
};
// --- Components ---
function EditOptionModal({
  isOpen,
  onClose,
  option,
  scope,
  onSave,
  onSaveGlobal
}: {
  isOpen: boolean;
  onClose: () => void;
  option: ResolvedOption;
  scope: Scope;
  onSave: (override: OptionOverride) => void;
  onSaveGlobal: (optionId: string, updates: Partial<GlobalOption>) => void;
}) {
  const [price, setPrice] = useState<string>(option.effectivePrice.toString());
  const [enabled, setEnabled] = useState<boolean>(option.effectiveEnabled);
  const [isStandard, setIsStandard] = useState<boolean>(option.effectiveIsStandard);
  const [name, setName] = useState<string>(option.effectiveName);
  const [description, setDescription] = useState<string>(option.effectiveDescription);
  const [overrideTypes, setOverrideTypes] = useState<Set<'price' | 'enabled' | 'content' | 'standard'>>(new Set());
  const isGlobal = scope.level === 'global';
  if (!isOpen) return null;
  const handleSave = () => {
    if (isGlobal) {
      const updates: Partial<GlobalOption> = {};
      if (overrideTypes.has('price')) updates.basePrice = parseFloat(price);
      if (overrideTypes.has('enabled')) updates.enabled = enabled;
      if (overrideTypes.has('standard')) updates.isStandard = isStandard;
      if (overrideTypes.has('content')) {
        updates.name = name;
        updates.description = description;
      }
      onSaveGlobal(option.id, updates);
    } else {
      const override: OptionOverride = {
        optionId: option.id,
        level: scope.level as 'region' | 'community',
        levelId: scope.level === 'region' ? scope.regionId! : scope.communityId!
      };
      if (overrideTypes.has('price')) override.priceOverride = parseFloat(price);
      if (overrideTypes.has('enabled')) override.enabledOverride = enabled;
      if (overrideTypes.has('standard')) override.isStandardOverride = isStandard;
      if (overrideTypes.has('content')) {
        override.nameOverride = name;
        override.descriptionOverride = description;
      }
      onSave(override);
    }
    onClose();
  };
  const toggleType = (type: 'price' | 'enabled' | 'content' | 'standard') => {
    const next = new Set(overrideTypes);
    if (next.has(type)) next.delete(type);else next.add(type);
    setOverrideTypes(next);
  };
  return <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-id="element-1758">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto" data-id="element-1759">
        <div className="flex items-center justify-between p-6 border-b border-slate-200 sticky top-0 bg-white" data-id="element-1760">
          <div data-id="element-1761">
            <h2 className="text-lg font-semibold text-slate-900" data-id="element-1762">
              {isGlobal ? 'Edit Option' : 'Override Option'}
            </h2>
            <p className="text-sm text-slate-500" data-id="element-1763">
              {isGlobal ? 'Editing global defaults (affects all regions & communities)' : <>
                  Customizing for{' '}
                  <span className="font-medium text-slate-900" data-id="element-1764">
                    {scope.name}
                  </span>
                </>}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-id="element-1765">
            <X className="h-5 w-5" data-id="element-1766" />
          </button>
        </div>

        <div className="p-6 space-y-6" data-id="element-1767">
          {/* Option Info */}
          <div className="bg-slate-50 p-4 rounded-lg flex gap-4" data-id="element-1768">
            {option.hasImage && option.imageUrl ? <img src={option.imageUrl} alt={option.name} className="w-16 h-16 object-cover rounded-md bg-white border border-slate-200" data-id="element-1769" /> : <div className="w-16 h-16 bg-white border border-slate-200 rounded-md flex items-center justify-center" data-id="element-1770">
                <ImageOff className="h-6 w-6 text-slate-300" data-id="element-1771" />
              </div>}
            <div className="flex-1" data-id="element-1772">
              <div className="flex items-center gap-2" data-id="element-1773">
                <h3 className="font-medium text-slate-900" data-id="element-1774">{option.name}</h3>
                {option.effectiveIsStandard && <Badge variant="success" className="text-xs" data-id="element-1775">
                    <Star className="h-3 w-3 mr-1" data-id="element-1776" /> Standard
                  </Badge>}
              </div>
              <p className="text-sm text-slate-500 line-clamp-2" data-id="element-1777">
                {option.description}
              </p>
            </div>
          </div>

          {/* Override Type Selectors */}
          <div className="space-y-4" data-id="element-1778">
            <p className="text-sm font-medium text-slate-700" data-id="element-1779">
              {isGlobal ? 'What do you want to edit?' : 'What do you want to override?'}
            </p>
            <div className="grid grid-cols-2 gap-3" data-id="element-1780">
              <button onClick={() => toggleType('price')} className={`py-2 px-3 rounded-md border text-sm font-medium transition-colors flex items-center justify-center gap-2 ${overrideTypes.has('price') ? 'bg-primary-50 border-primary-200 text-primary-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-1781">
                <DollarSign className="h-4 w-4" data-id="element-1782" /> Price
              </button>
              <button onClick={() => toggleType('standard')} className={`py-2 px-3 rounded-md border text-sm font-medium transition-colors flex items-center justify-center gap-2 ${overrideTypes.has('standard') ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-1783">
                <Star className="h-4 w-4" data-id="element-1784" /> Standard
              </button>
              <button onClick={() => toggleType('enabled')} className={`py-2 px-3 rounded-md border text-sm font-medium transition-colors ${overrideTypes.has('enabled') ? 'bg-primary-50 border-primary-200 text-primary-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-1785">
                Availability
              </button>
              <button onClick={() => toggleType('content')} className={`py-2 px-3 rounded-md border text-sm font-medium transition-colors ${overrideTypes.has('content') ? 'bg-primary-50 border-primary-200 text-primary-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-1786">
                Title/Desc
              </button>
            </div>
          </div>

          {/* Input Fields */}
          <div className="space-y-4 pt-2 border-t border-slate-100" data-id="element-1787">
            {overrideTypes.has('standard') && <div className="p-4 rounded-lg border border-emerald-200 bg-emerald-50/50" data-id="element-1788">
                <div className="flex items-center justify-between" data-id="element-1789">
                  <div data-id="element-1790">
                    <label className="block text-sm font-medium text-slate-900" data-id="element-1791">
                      Include as Standard
                    </label>
                    <p className="text-xs text-slate-500 mt-1" data-id="element-1792">
                      {isStandard ? 'This option is INCLUDED at no extra cost' : 'This option is an UPGRADE (costs extra)'}
                    </p>
                    {!isGlobal && <p className="text-xs text-emerald-600 mt-1" data-id="element-1793">
                        Parent:{' '}
                        {option.parentValue?.isStandard ?? option.isStandard ? 'Standard' : 'Upgrade'}
                      </p>}
                  </div>
                  <Toggle checked={isStandard} onChange={setIsStandard} label={isStandard ? 'Standard' : 'Upgrade'} data-id="element-1794" />
                </div>
                {isStandard && <p className="text-xs text-emerald-700 mt-3 p-2 bg-emerald-100 rounded" data-id="element-1795">
                    💡 When marked as Standard, this option will show as
                    "Standard" for customers in{' '}
                    {isGlobal ? 'all communities' : scope.name}.
                  </p>}
              </div>}

            {overrideTypes.has('price') && <div data-id="element-1796">
                <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1797">
                  {isGlobal ? 'Base Price' : 'Override Price'}
                </label>
                <div className="flex items-center gap-3" data-id="element-1798">
                  <div className="relative flex-1" data-id="element-1799">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" data-id="element-1800">
                      $
                    </span>
                    <input type="number" value={price} onChange={e => setPrice(e.target.value)} className="w-full pl-7 pr-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-1801" />
                  </div>
                  {!isGlobal && <div className="text-sm text-slate-500" data-id="element-1802">
                      was{' '}
                      <span className="line-through" data-id="element-1803">
                        $
                        {(option.parentValue?.price ?? option.basePrice).toLocaleString()}
                      </span>
                    </div>}
                </div>
                {isStandard && parseFloat(price) > 0 && <p className="text-xs text-amber-600 mt-1" data-id="element-1804">
                    ⚠️ Standard options typically have $0 price since they're
                    included.
                  </p>}
              </div>}

            {overrideTypes.has('enabled') && <div className="flex items-center justify-between" data-id="element-1805">
                <div data-id="element-1806">
                  <label className="block text-sm font-medium text-slate-700" data-id="element-1807">
                    Availability Status
                  </label>
                  <p className="text-xs text-slate-500" data-id="element-1808">
                    Currently:{' '}
                    {option.parentValue?.enabled ?? option.enabled ? 'Enabled' : 'Disabled'}
                  </p>
                </div>
                <Toggle checked={enabled} onChange={setEnabled} label={enabled ? 'Enabled' : 'Disabled'} data-id="element-1809" />
              </div>}

            {overrideTypes.has('content') && <div className="space-y-4" data-id="element-1810">
                <div data-id="element-1811">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1812">
                    {isGlobal ? 'Title' : 'Override Title'}
                  </label>
                  <input type="text" value={name} onChange={e => setName(e.target.value)} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-1813" />
                </div>
                <div data-id="element-1814">
                  <label className="block text-sm font-medium text-slate-700 mb-1" data-id="element-1815">
                    {isGlobal ? 'Description' : 'Override Description'}
                  </label>
                  <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-1816" />
                </div>
              </div>}

            {overrideTypes.size === 0 && <div className="text-center py-4 text-slate-500 text-sm italic" data-id="element-1817">
                Select an attribute above to start{' '}
                {isGlobal ? 'editing' : 'customizing'}
              </div>}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-slate-200 sticky bottom-0 bg-white" data-id="element-1818">
          <Button variant="ghost" onClick={onClose} data-id="element-1819">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={overrideTypes.size === 0} data-id="element-1820">
            {isGlobal ? 'Save Changes' : 'Save Override'}
          </Button>
        </div>
      </div>
    </div>;
}
function ScopeSelector({
  currentScope,
  onChange
}: {
  currentScope: Scope;
  onChange: (scope: Scope) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return {
      regions,
      communities
    };
    const query = searchQuery.toLowerCase();
    const filteredRegions = regions.filter(r => r.name.toLowerCase().includes(query));
    const filteredCommunities: typeof communities = {};
    Object.entries(communities).forEach(([regionId, comms]) => {
      const matchingComms = comms.filter(c => c.name.toLowerCase().includes(query));
      if (matchingComms.length > 0) {
        filteredCommunities[regionId] = matchingComms;
        if (!filteredRegions.find(r => r.id === regionId)) {
          const region = regions.find(r => r.id === regionId);
          if (region) filteredRegions.push(region);
        }
      }
    });
    return {
      regions: filteredRegions,
      communities: filteredCommunities
    };
  }, [searchQuery]);
  const handleSelect = (scope: Scope) => {
    onChange(scope);
    setIsOpen(false);
    setSearchQuery('');
  };
  return <div className="relative" data-id="element-1821">
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors w-full" data-id="element-1822">
        {currentScope.level === 'global' && <Globe className="h-4 w-4 text-slate-500" data-id="element-1823" />}
        {currentScope.level === 'region' && <MapPin className="h-4 w-4 text-blue-500" data-id="element-1824" />}
        {currentScope.level === 'community' && <Building className="h-4 w-4 text-amber-500" data-id="element-1825" />}
        <div className="flex-1 text-left" data-id="element-1826">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold" data-id="element-1827">
            Viewing Catalog
          </p>
          <p className="text-sm font-medium text-slate-900 truncate" data-id="element-1828">
            {currentScope.name}
          </p>
        </div>
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-1829" />
      </button>

      {isOpen && <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50 max-h-[400px] flex flex-col" data-id="element-1830">
          <div className="px-3 pb-2 border-b border-slate-100" data-id="element-1831">
            <div className="relative" data-id="element-1832">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-1833" />
              <input type="text" placeholder="Search regions or communities..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-md focus:ring-primary-500 focus:border-primary-500" autoFocus data-id="element-1834" />
            </div>
          </div>

          <div className="overflow-y-auto flex-1" data-id="element-1835">
            {(!searchQuery || 'global catalog'.includes(searchQuery.toLowerCase())) && <button onClick={() => handleSelect({
          level: 'global',
          name: 'Global Catalog'
        })} className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-slate-50 ${currentScope.level === 'global' ? 'bg-slate-50' : ''}`} data-id="element-1836">
                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center" data-id="element-1837">
                  <Globe className="h-4 w-4 text-slate-600" data-id="element-1838" />
                </div>
                <div data-id="element-1839">
                  <p className="text-sm font-medium text-slate-900" data-id="element-1840">
                    Global Catalog
                  </p>
                  <p className="text-xs text-slate-500" data-id="element-1841">Master options list</p>
                </div>
                {currentScope.level === 'global' && <Check className="h-4 w-4 text-primary-600 ml-auto" data-id="element-1842" />}
              </button>}

            {filteredData.regions.length > 0 && <>
                <div className="my-2 border-t border-slate-100" data-id="element-1843" />
                <div className="px-4 py-1 text-xs font-semibold text-slate-400 uppercase tracking-wider" data-id="element-1844">
                  Regions & Communities
                </div>

                {filteredData.regions.map(region => <div key={region.id} data-id="element-1845">
                    <button onClick={() => handleSelect({
              level: 'region',
              regionId: region.id,
              name: region.name
            })} className={`w-full px-4 py-2 text-left flex items-center gap-3 hover:bg-slate-50 ${currentScope.level === 'region' && currentScope.regionId === region.id ? 'bg-slate-50' : ''}`} data-id="element-1846">
                      <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center" data-id="element-1847">
                        <MapPin className="h-4 w-4 text-blue-600" data-id="element-1848" />
                      </div>
                      <div className="flex-1" data-id="element-1849">
                        <p className="text-sm font-medium text-slate-900" data-id="element-1850">
                          {region.name}
                        </p>
                      </div>
                      {currentScope.level === 'region' && currentScope.regionId === region.id && <Check className="h-4 w-4 text-primary-600" data-id="element-1851" />}
                    </button>

                    {(filteredData.communities[region.id] || communities[region.id])?.map(community => {
              if (searchQuery && !community.name.toLowerCase().includes(searchQuery.toLowerCase())) return null;
              return <button key={community.id} onClick={() => handleSelect({
                level: 'community',
                regionId: region.id,
                communityId: community.id,
                name: community.name
              })} className={`w-full py-1.5 px-2 ml-12 mr-4 rounded text-left flex items-center gap-2 hover:bg-slate-50 ${currentScope.level === 'community' && currentScope.communityId === community.id ? 'bg-amber-50 text-amber-900' : 'text-slate-600'}`} data-id="element-1852">
                          <Building className="h-3 w-3" data-id="element-1853" />
                          <span className="text-sm" data-id="element-1854">{community.name}</span>
                          {currentScope.level === 'community' && currentScope.communityId === community.id && <Check className="h-3 w-3 ml-auto" data-id="element-1855" />}
                        </button>;
            })}
                  </div>)}
              </>}

            {searchQuery && filteredData.regions.length === 0 && <div className="px-4 py-6 text-center text-sm text-slate-500" data-id="element-1856">
                No regions or communities found for "{searchQuery}"
              </div>}
          </div>
        </div>}
    </div>;
}
// --- Main Page Component ---
export function OptionsAltPage() {
  const [scope, setScope] = useState<Scope>({
    level: 'global',
    name: 'Global Catalog'
  });
  const [globalOptions, setGlobalOptions] = useState<GlobalOption[]>(initialGlobalOptions);
  const [overrides, setOverrides] = useState<OptionOverride[]>(initialOverrides);
  const [searchQuery, setSearchQuery] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedOptionForEdit, setSelectedOptionForEdit] = useState<ResolvedOption | null>(null);
  const resolvedOptions = useMemo(() => {
    return globalOptions.map(opt => resolveOption(opt, scope, overrides));
  }, [scope, overrides, globalOptions]);
  const currentLevel: NavigationLevel = breadcrumbs.length === 0 ? 'category' : breadcrumbs.length === 1 ? 'subcategory' : breadcrumbs.length === 2 ? 'variantSet' : 'options';
  const getSubcategories = (category: string) => {
    return Array.from(new Set(resolvedOptions.filter(o => o.category === category).map(o => o.subcategory)));
  };
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);else next.add(category);
      return next;
    });
  };
  const currentItems = useMemo(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      return resolvedOptions.filter(opt => opt.name.toLowerCase().includes(query) || opt.effectiveDescription?.toLowerCase().includes(query) || opt.tags?.some(tag => tag.toLowerCase().includes(query)));
    }
    if (breadcrumbs.length === 0) {
      const categories = Array.from(new Set(resolvedOptions.map(o => o.category)));
      return categories.map(cat => {
        const items = resolvedOptions.filter(o => o.category === cat);
        return {
          id: cat,
          name: cat,
          count: items.length,
          enabledCount: items.filter(o => o.effectiveEnabled).length,
          type: 'category' as const
        };
      });
    }
    if (breadcrumbs.length === 1) {
      const category = breadcrumbs[0].value;
      const subcategories = Array.from(new Set(resolvedOptions.filter(o => o.category === category).map(o => o.subcategory)));
      return subcategories.map(subcat => {
        const items = resolvedOptions.filter(o => o.category === category && o.subcategory === subcat);
        return {
          id: subcat,
          name: subcat,
          count: items.length,
          enabledCount: items.filter(o => o.effectiveEnabled).length,
          type: 'subcategory' as const
        };
      });
    }
    if (breadcrumbs.length === 2) {
      const category = breadcrumbs[0].value;
      const subcategory = breadcrumbs[1].value;
      if (category === 'Models') {
        const items: any[] = [];
        const modelOption = resolvedOptions.find(o => o.category === category && o.subcategory === subcategory && o.variantSet === 'Model Details');
        if (modelOption) items.push({
          ...modelOption,
          type: 'model-card' as const
        });
        const variantSets = Array.from(new Set(resolvedOptions.filter(o => o.category === category && o.subcategory === subcategory && o.variantSet !== 'Model Details').map(o => o.variantSet)));
        variantSets.forEach(vs => {
          const vsItems = resolvedOptions.filter(o => o.category === category && o.subcategory === subcategory && o.variantSet === vs);
          items.push({
            id: vs,
            name: vs,
            count: vsItems.length,
            enabledCount: vsItems.filter(o => o.effectiveEnabled).length,
            type: 'variantSet' as const
          });
        });
        return items;
      }
      const variantSets = Array.from(new Set(resolvedOptions.filter(o => o.category === category && o.subcategory === subcategory).map(o => o.variantSet)));
      return variantSets.map(vs => {
        const items = resolvedOptions.filter(o => o.category === category && o.subcategory === subcategory && o.variantSet === vs);
        return {
          id: vs,
          name: vs,
          count: items.length,
          enabledCount: items.filter(o => o.effectiveEnabled).length,
          type: 'variantSet' as const
        };
      });
    }
    const category = breadcrumbs[0].value;
    const subcategory = breadcrumbs[1].value;
    const variantSet = breadcrumbs[2].value;
    return resolvedOptions.filter(o => o.category === category && o.subcategory === subcategory && o.variantSet === variantSet);
  }, [breadcrumbs, resolvedOptions, searchQuery]);
  const isShowingOptions = currentLevel === 'options' || searchQuery.trim().length > 0;
  const handleNavigate = (item: any) => {
    if (item.type === 'category') setBreadcrumbs([{
      label: item.name,
      level: 'category',
      value: item.name
    }]);else if (item.type === 'subcategory') setBreadcrumbs([...breadcrumbs, {
      label: item.name,
      level: 'subcategory',
      value: item.name
    }]);else if (item.type === 'variantSet') setBreadcrumbs([...breadcrumbs, {
      label: item.name,
      level: 'variantSet',
      value: item.name
    }]);
  };
  const handleSidebarNavigate = (category: string, subcategory?: string) => {
    if (subcategory) setBreadcrumbs([{
      label: category,
      level: 'category',
      value: category
    }, {
      label: subcategory,
      level: 'subcategory',
      value: subcategory
    }]);else setBreadcrumbs([{
      label: category,
      level: 'category',
      value: category
    }]);
  };
  const handleBreadcrumbClick = (index: number) => setBreadcrumbs(breadcrumbs.slice(0, index + 1));
  const handleOpenEdit = (option: ResolvedOption) => {
    setSelectedOptionForEdit(option);
    setEditModalOpen(true);
    setOpenDropdown(null);
  };
  const handleSaveOverride = (override: OptionOverride) => {
    const filtered = overrides.filter(o => !(o.optionId === override.optionId && o.level === override.level && o.levelId === override.levelId));
    setOverrides([...filtered, override]);
  };
  const handleSaveGlobal = (optionId: string, updates: Partial<GlobalOption>) => {
    setGlobalOptions(prev => prev.map(opt => opt.id === optionId ? {
      ...opt,
      ...updates
    } : opt));
  };
  const handleResetOverride = (option: ResolvedOption) => {
    if (scope.level === 'global') return;
    const levelId = scope.level === 'region' ? scope.regionId : scope.communityId;
    setOverrides(overrides.filter(o => !(o.optionId === option.id && o.level === scope.level && o.levelId === levelId)));
    setOpenDropdown(null);
  };
  const stats = useMemo(() => {
    const total = resolvedOptions.length;
    const enabled = resolvedOptions.filter(o => o.effectiveEnabled).length;
    const standard = resolvedOptions.filter(o => o.effectiveIsStandard).length;
    const overridden = resolvedOptions.filter(o => o.hasOverride && o.overrideSource === scope.level).length;
    const inherited = resolvedOptions.filter(o => scope.level !== 'global' && o.overrideSource !== scope.level && o.overrideSource !== 'global').length;
    return {
      total,
      enabled,
      standard,
      overridden,
      inherited
    };
  }, [resolvedOptions, scope]);
  // Render option card helper
  const renderOptionCard = (option: ResolvedOption, isGrid: boolean = true) => <div key={option.id} className={`bg-white border rounded-lg p-4 flex gap-4 relative transition-all ${option.effectiveEnabled ? 'border-slate-200 shadow-sm' : 'border-slate-100 opacity-75 bg-slate-50'}`} data-id="element-1857">
      <div className={`${isGrid ? 'h-20 w-20' : 'h-12 w-12'} bg-slate-100 rounded-md flex-shrink-0 flex items-center justify-center border border-slate-100 overflow-hidden`} data-id="element-1858">
        {option.hasImage && option.imageUrl ? <img src={option.imageUrl} alt={option.effectiveName} className="h-full w-full object-cover" data-id="element-1859" /> : <ImageOff className={`${isGrid ? 'h-8 w-8' : 'h-5 w-5'} text-slate-300`} data-id="element-1860" />}
      </div>
      <div className="flex-1 min-w-0 flex flex-col" data-id="element-1861">
        <div className="flex items-start justify-between mb-1" data-id="element-1862">
          <div className="flex items-center gap-2 min-w-0" data-id="element-1863">
            <h4 className="font-medium text-slate-900 truncate" data-id="element-1864">
              {option.effectiveName}
            </h4>
            {option.effectiveIsStandard && <Badge variant="success" className="text-[10px] px-1.5 py-0.5 flex-shrink-0" data-id="element-1865">
                <Star className="h-3 w-3 mr-0.5" data-id="element-1866" /> Included
              </Badge>}
          </div>
          <div className="relative flex-shrink-0" data-id="element-1867">
            <button onClick={() => setOpenDropdown(openDropdown === option.id ? null : option.id)} className="p-1 hover:bg-slate-100 rounded" data-id="element-1868">
              <MoreVertical className="h-4 w-4 text-slate-400" data-id="element-1869" />
            </button>
            {openDropdown === option.id && <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-10 min-w-[200px]" data-id="element-1870">
                <button onClick={() => handleOpenEdit(option)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50" data-id="element-1871">
                  <Edit className="h-4 w-4" data-id="element-1872" />{' '}
                  {scope.level === 'global' ? 'Edit Option' : `Override for ${scope.level}`}
                </button>
                {scope.level !== 'global' && option.hasOverride && option.overrideSource === scope.level && <button onClick={() => handleResetOverride(option)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-1873">
                      <Undo2 className="h-4 w-4" data-id="element-1874" /> Reset to Parent
                    </button>}
              </div>}
          </div>
        </div>
        {isGrid && <p className="text-xs text-slate-500 line-clamp-2 mb-2" data-id="element-1875">
            {option.effectiveDescription}
          </p>}
        <div className={`flex items-center justify-between ${isGrid ? 'mt-auto' : ''}`} data-id="element-1876">
          <div className="flex items-center gap-2" data-id="element-1877">
            {option.effectiveIsStandard ? <p className="text-sm font-semibold text-emerald-600" data-id="element-1878">Standard</p> : <>
                <p className="text-sm font-semibold text-slate-900" data-id="element-1879">
                  ${option.effectivePrice.toLocaleString()}
                </p>
                {option.hasOverride && option.parentValue && option.parentValue.price !== option.effectivePrice && <p className="text-xs text-slate-400 line-through" data-id="element-1880">
                      ${option.parentValue.price.toLocaleString()}
                    </p>}
              </>}
          </div>
          {scope.level !== 'global' && <Badge variant="neutral" className={`text-[10px] px-1.5 py-0.5 ${option.hasOverride && option.overrideSource === scope.level ? 'bg-amber-50 text-amber-700 border-amber-200' : option.overrideSource !== 'global' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-100 text-slate-600'}`} data-id="element-1881">
              {option.hasOverride && option.overrideSource === scope.level ? <span className="flex items-center gap-1" data-id="element-1882">
                  <Edit className="h-3 w-3" data-id="element-1883" /> Overridden
                </span> : option.overrideSource !== 'global' ? <span className="flex items-center gap-1" data-id="element-1884">
                  <RefreshCw className="h-3 w-3" data-id="element-1885" /> Inherited
                </span> : <span className="flex items-center gap-1" data-id="element-1886">
                  <Globe className="h-3 w-3" data-id="element-1887" /> Global
                </span>}
            </Badge>}
        </div>
      </div>
    </div>;
  return <div className="flex flex-col h-[calc(100vh-8rem)]" data-id="element-1888">
      {selectedOptionForEdit && <EditOptionModal isOpen={editModalOpen} onClose={() => {
      setEditModalOpen(false);
      setSelectedOptionForEdit(null);
    }} option={selectedOptionForEdit} scope={scope} onSave={handleSaveOverride} onSaveGlobal={handleSaveGlobal} data-id="element-1889" />}

      <div className="flex gap-6 flex-1 overflow-hidden" data-id="element-1890">
        {/* Sidebar */}
        <div className="w-64 flex-shrink-0 flex flex-col gap-4" data-id="element-1891">
          <ScopeSelector currentScope={scope} onChange={setScope} data-id="element-1892" />
          <Card className="flex-1 flex flex-col overflow-hidden" noPadding data-id="element-1893">
            <div className="p-4 border-b border-slate-200" data-id="element-1894">
              <h3 className="font-semibold text-slate-900" data-id="element-1895">Categories</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-2" data-id="element-1896">
              <button onClick={() => setBreadcrumbs([])} className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${breadcrumbs.length === 0 ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-1897">
                <Home className="h-4 w-4" data-id="element-1898" />
                All Categories
              </button>
              <div className="mt-2 space-y-1" data-id="element-1899">
                {['Models', 'Exterior', 'Interior'].map(cat => {
                const isActive = breadcrumbs[0]?.value === cat;
                const isExpanded = expandedCategories.has(cat);
                const items = resolvedOptions.filter(o => o.category === cat);
                const subcategories = getSubcategories(cat);
                return <div key={cat} data-id="element-1900">
                      <div className="flex items-center gap-1" data-id="element-1901">
                        <button onClick={() => toggleCategory(cat)} className="p-1 hover:bg-slate-100 rounded transition-colors" data-id="element-1902">
                          {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" data-id="element-1903" /> : <ChevronRight className="h-4 w-4 text-slate-400" data-id="element-1904" />}
                        </button>
                        <button onClick={() => handleSidebarNavigate(cat)} className={`flex-1 flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${isActive && breadcrumbs.length === 1 ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-1905">
                          <span data-id="element-1906">{cat}</span>
                          <Badge variant={isActive && breadcrumbs.length === 1 ? 'primary' : 'neutral'} className="text-xs" data-id="element-1907">
                            {items.length}
                          </Badge>
                        </button>
                      </div>
                      {isExpanded && <div className="ml-6 mt-1 space-y-1" data-id="element-1908">
                          {subcategories.map(subcat => {
                      const isSubActive = breadcrumbs[0]?.value === cat && breadcrumbs[1]?.value === subcat;
                      const subcatItems = resolvedOptions.filter(o => o.category === cat && o.subcategory === subcat);
                      return <button key={subcat} onClick={() => handleSidebarNavigate(cat, subcat)} className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors ${isSubActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-1909">
                                <span className="truncate" data-id="element-1910">{subcat}</span>
                                <Badge variant={isSubActive ? 'primary' : 'neutral'} className="text-xs ml-2" data-id="element-1911">
                                  {subcatItems.length}
                                </Badge>
                              </button>;
                    })}
                        </div>}
                    </div>;
              })}
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden" data-id="element-1912">
          <div className="flex items-center justify-between mb-6" data-id="element-1913">
            <div data-id="element-1914">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-1915">
                Options Catalog
              </h1>
              <p className="text-sm text-slate-500" data-id="element-1916">
                Manage and override options across levels
              </p>
            </div>
            <Button leftIcon={<Save className="h-4 w-4" data-id="element-1918" />} data-id="element-1917">
              Save Changes
            </Button>
          </div>

          {/* Stats Bar */}
          <div className="grid grid-cols-5 gap-4 mb-6" data-id="element-1919">
            <Card noPadding data-id="element-1920">
              <div className="p-4" data-id="element-1921">
                <p className="text-sm text-slate-500 mb-1" data-id="element-1922">Total Options</p>
                <p className="text-2xl font-bold text-slate-900" data-id="element-1923">
                  {stats.total}
                </p>
              </div>
            </Card>
            <Card noPadding data-id="element-1924">
              <div className="p-4" data-id="element-1925">
                <p className="text-sm text-slate-500 mb-1" data-id="element-1926">Enabled</p>
                <p className="text-2xl font-bold text-green-600" data-id="element-1927">
                  {stats.enabled}
                </p>
              </div>
            </Card>
            <Card noPadding data-id="element-1928">
              <div className="p-4" data-id="element-1929">
                <p className="text-sm text-slate-500 mb-1" data-id="element-1930">Standard</p>
                <div className="flex items-center gap-2" data-id="element-1931">
                  <p className="text-2xl font-bold text-emerald-600" data-id="element-1932">
                    {stats.standard}
                  </p>
                  <Badge variant="success" className="text-xs" data-id="element-1933">
                    <Star className="h-3 w-3" data-id="element-1934" />
                  </Badge>
                </div>
              </div>
            </Card>
            <Card noPadding className={scope.level === 'global' ? 'opacity-50' : ''} data-id="element-1935">
              <div className="p-4" data-id="element-1936">
                <p className="text-sm text-slate-500 mb-1" data-id="element-1937">Inherited</p>
                <p className="text-2xl font-bold text-blue-600" data-id="element-1938">
                  {stats.inherited}
                </p>
              </div>
            </Card>
            <Card noPadding className={scope.level === 'global' ? 'opacity-50' : ''} data-id="element-1939">
              <div className="p-4" data-id="element-1940">
                <p className="text-sm text-slate-500 mb-1" data-id="element-1941">Overridden</p>
                <p className="text-2xl font-bold text-amber-600" data-id="element-1942">
                  {stats.overridden}
                </p>
              </div>
            </Card>
          </div>

          {/* Search & Breadcrumbs */}
          <Card className="mb-6" noPadding data-id="element-1943">
            <div className="p-4 flex flex-col gap-4" data-id="element-1944">
              <div className="flex items-center gap-3" data-id="element-1945">
                <div className="flex-1 relative" data-id="element-1946">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" data-id="element-1947" />
                  <input type="text" placeholder="Search options..." className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-1948" />
                </div>
                {isShowingOptions && <div className="flex border border-slate-300 rounded-md overflow-hidden" data-id="element-1949">
                    <button onClick={() => setViewMode('grid')} className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-1950">
                      <Grid3x3 className="h-4 w-4" data-id="element-1951" />
                    </button>
                    <button onClick={() => setViewMode('list')} className={`px-3 py-2 border-l border-slate-300 ${viewMode === 'list' ? 'bg-slate-100 text-slate-900' : 'text-slate-600 hover:bg-slate-50'}`} data-id="element-1952">
                      <List className="h-4 w-4" data-id="element-1953" />
                    </button>
                  </div>}
              </div>
              {breadcrumbs.length > 0 && !searchQuery && <div className="flex items-center gap-2 text-sm text-slate-600 pt-2 border-t border-slate-100" data-id="element-1954">
                  <button onClick={() => setBreadcrumbs([])} className="hover:text-slate-900" data-id="element-1955">
                    Home
                  </button>
                  {breadcrumbs.map((crumb, index) => <Fragment key={index} data-id="element-1956">
                      <ChevronRight className="h-4 w-4 text-slate-400" data-id="element-1957" />
                      <button onClick={() => handleBreadcrumbClick(index)} className={`hover:text-slate-900 ${index === breadcrumbs.length - 1 ? 'font-medium text-slate-900' : ''}`} data-id="element-1958">
                        {crumb.label}
                      </button>
                    </Fragment>)}
                </div>}
            </div>
          </Card>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto pb-8" data-id="element-1959">
            {!isShowingOptions ? <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-id="element-1960">
                {currentItems.map((item: any) => {
              if (item.type === 'model-card') return renderOptionCard(item as ResolvedOption, true);
              return <button key={item.id} onClick={() => handleNavigate(item)} className="bg-white border border-slate-200 rounded-lg p-6 hover:border-primary-300 hover:shadow-md transition-all text-left group" data-id="element-1961">
                      <div className="flex items-start justify-between mb-3" data-id="element-1962">
                        <h4 className="text-lg font-semibold text-slate-900 group-hover:text-primary-600 transition-colors" data-id="element-1963">
                          {item.name}
                        </h4>
                        <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-primary-500 transition-colors" data-id="element-1964" />
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600" data-id="element-1965">
                        <span data-id="element-1966">{item.count} options</span>
                        <span className="text-green-600" data-id="element-1967">
                          {item.enabledCount} enabled
                        </span>
                      </div>
                    </button>;
            })}
              </div> : <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4' : 'space-y-2'} data-id="element-1968">
                {currentItems.map(item => renderOptionCard(item as ResolvedOption, viewMode === 'grid'))}
              </div>}
          </div>
        </div>
      </div>
    </div>;
}